/**
 * API service for Job Order Ingestion
 * Upload a customer job-order document (PDF/xlsx/csv), AI-parse it into a
 * reviewable draft, then confirm to create (or replace) a work order with
 * attribute values and BOM part lines.
 */
import axios from "../utils/api-client";

const API_BASE_URL = `${process.env.REACT_APP_API_END_POINT}/OrderIngestion`;

export interface JobOrderVehicle {
	vin?: string;
	unitNo?: string;
	year?: string;
	make?: string;
	model?: string;
}

export interface JobOrderLaborPhase {
	phase?: string;
	description?: string;
	estHours?: number;
}

export interface JobOrderOperation {
	name?: string;
	estHours?: number;
	notes?: string;
}

export interface JobOrderPartLineDraft {
	operationLabel?: string;
	phaseCode?: string;
	partNumber?: string;
	description?: string;
	supplierNumber?: string;
	supplierName?: string;
	qty?: number;
}

/** AI-parsed draft of a job order document; edited in the review UI */
export interface JobOrderDraft {
	jobOrderNo?: string;
	revision?: string;
	customer?: string;
	specCode?: string;
	description?: string;
	/** yyyy-MM-dd */
	startDate?: string;
	vehicle?: JobOrderVehicle;
	laborPhases: JobOrderLaborPhase[];
	operations: JobOrderOperation[];
	partLines: JobOrderPartLineDraft[];
}

export interface JobOrderProductMatch {
	productId: number;
	itemNumber: string;
	name: string;
}

export interface JobOrderLaborStandardCandidate {
	id: number;
	workstation: string;
	batchSize: number;
	totalDurationMinutes: number;
	description?: string;
	displayName: string;
}

export interface JobOrderExistingOrder {
	id: number;
	workstation: string;
	startTime: string;
	revision?: string;
	jobOrderNo?: string;
}

export interface JobOrderParseResponse {
	success: boolean;
	error?: string;
	draft?: JobOrderDraft;
	processingTimeMs: number;
	warnings: string[];
	productMatch?: JobOrderProductMatch;
	laborStandardCandidates: JobOrderLaborStandardCandidate[];
	existingOrder?: JobOrderExistingOrder;
}

export type RevisionAction = "create" | "replace" | "skip";

export interface JobOrderConfirmRequest {
	companyId: number;
	draft: JobOrderDraft;
	/** Work center name chosen in the review UI */
	workstation: string;
	laborStandardId?: number;
	productId?: number;
	revisionAction: RevisionAction;
	/** Required when revisionAction is "replace" */
	existingWorkOrderId?: number;
	/** Reviewed attribute values keyed by the company's attrKeys */
	attributeValues?: Record<string, string>;
}

export interface JobOrderConfirmResponse {
	workOrderId?: number;
	action: "created" | "replaced" | "skipped";
	partLinesImported: number;
	message?: string;
	warnings: string[];
}

export const orderIngestionApi = {
	/**
	 * Parse an uploaded job-order document (pdf/xlsx/csv) into a draft with
	 * server-side enrichment (product/labor-standard match, conflict detection)
	 */
	parse: async (file: File, companyId: number): Promise<JobOrderParseResponse> => {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("companyId", String(companyId));

		const response = await axios.post<JobOrderParseResponse>(
			`${API_BASE_URL}/parse`,
			formData,
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
			}
		);
		return response.data;
	},

	/**
	 * Confirm a reviewed draft: create the work order, replace the existing
	 * one (revision update), or skip
	 */
	confirm: async (request: JobOrderConfirmRequest): Promise<JobOrderConfirmResponse> => {
		const response = await axios.post<JobOrderConfirmResponse>(
			`${API_BASE_URL}/confirm`,
			request
		);
		return response.data;
	},
};

export default orderIngestionApi;
